import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { SkeletonText } from "~/components/skeleton/text";
import type { IdOrIdentifier } from "~/tools/id-or-identifier";

import { DashboardScopeIcon } from "../../../components/_dashboard-scope-icon";
import { DashboardStar } from "../../../components/dashboard-star";
import { useNavigateToLink } from "../../../components/react/link";
import { queryDashboard } from "../../../queries/dashboards";
import { useQuery } from "../../../queries/react-query";
import { BreadcrumbLayout } from "../../breadcrumbs/BreadcrumbLayout";
import { DASHBOARD_PATH } from "../../Routes";

import { useDashboardContext } from "./_dashboard-context";

export interface DashboardBreadcrumbProps {
	idOrIdentifier: IdOrIdentifier;
}

export const DashboardBreadcrumb: React.FC<DashboardBreadcrumbProps> = ({ idOrIdentifier }) => {
	const dashboardsOverviewLink = useNavigateToLink(DASHBOARD_PATH, {
		params: { idOrIdentifier },
		search: ["timeRange", "timestamp", "detachedFilters"],
	});

	const { data: dashboard, isError } = useQuery(queryDashboard(idOrIdentifier));

	const { state } = useDashboardContext();
	const isDashboardDirty = useComputed((monitor) => state.form.isDirty(monitor), [state]);

	if (isError) {
		return <BreadcrumbLayout label="Unknown dashboard" />;
	}

	return (
		<BreadcrumbLayout
			label={
				<div className="flex items-center">
					<DashboardScopeIcon className="mr-2" dashboardScope={dashboard?.scope} variant="light" />

					<SkeletonText className="skeleton:min-w-60" when={dashboard?.name}>
						{(name) => (isDashboardDirty ? `${name} *` : name)}
					</SkeletonText>

					{/* pass identifier from loaded dashboard so the star skeleton is in sync with the text */}
					<DashboardStar className="relative -mr-1 ml-2" id={dashboard?.id} />
				</div>
			}
			to={dashboardsOverviewLink}
		/>
	);
};
